import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { signupUrlForPlan } from "../constants";

interface Tier {
  id: "free" | "pro" | "analyst";
  name: string;
  price: string;
  cadence: string;
  bullets: string[];
  cta: string;
  highlighted?: boolean;
}

const TIERS: Tier[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    bullets: [
      "Two-pane trading dashboard",
      "News headlines + clusters",
      "Basic in-app alerts",
    ],
    cta: "Get started free",
  },
  {
    id: "pro",
    name: "Pro",
    // Keep in sync with billing/PricingPage.tsx — this is the price the
    // user actually pays at Stripe checkout. The full page is the
    // canonical source of truth; the teaser must match.
    price: "$19.99",
    cadence: "/month",
    bullets: [
      "Everything in Free",
      "Tapelens AI Copilot",
      "Full historical data",
      "Advanced alerts & filters",
    ],
    cta: "Start 14-day free trial",
    highlighted: true,
  },
  {
    id: "analyst",
    name: "Analyst",
    price: "$49.99",
    cadence: "/month",
    bullets: [
      "Everything in Pro",
      "AI Copilot on our smarter model",
      "Premium analyses & briefings",
    ],
    cta: "Start 14-day free trial",
  },
];

export function PricingTeaser() {
  return (
    <section id="pricing" className="container mx-auto px-4 py-16">
      <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight">
        Simple, founder-friendly pricing
      </h2>
      <div className="grid gap-6 lg:grid-cols-3">
        {TIERS.map((tier) => (
          <div
            key={tier.id}
            className={`relative rounded-lg border bg-card p-6 ${
              tier.highlighted ? "border-primary shadow-lg" : "border-border"
            }`}
          >
            {tier.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                Most popular
              </span>
            )}
            <h3 className="mb-1 text-xl font-semibold">{tier.name}</h3>
            <div className="mb-6 flex items-baseline gap-1">
              <span className="text-3xl font-bold">{tier.price}</span>
              <span className="text-sm text-muted-foreground">{tier.cadence}</span>
            </div>
            <ul className="mb-6 space-y-2 text-sm">
              {tier.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <a
              href={signupUrlForPlan(tier.id)}
              className={`block rounded-md px-4 py-2 text-center text-sm font-medium ${
                tier.highlighted
                  ? "bg-primary text-primary-foreground hover:opacity-90"
                  : "border border-border text-foreground hover:bg-accent"
              }`}
            >
              {tier.cta}
            </a>
          </div>
        ))}
      </div>
      <p className="mt-8 text-center text-sm text-muted-foreground">
        Annual billing saves ~20% —{" "}
        <Link to="/pricing" className="text-primary hover:underline">
          See full pricing
        </Link>{" "}
        for details.
      </p>
    </section>
  );
}
